import React from 'react';

import Tippy from '@tippyjs/react';

import Icon from 'components/shared/Icon/Icon';
import Input from 'components/shared/Input/Input';
import Typography from 'components/shared/Typography/Typography';

import 'tippy.js/dist/tippy.css';

const TOOLTIP_CONTENT = {
  price: {
    medical: {
      example: 'Example:',
      multiplier: '• Multiplier is set to: 200%',
      service: '• Service: 99212 — Established patient office visit (10 - 19 min)',
      allowable: '• Allowable amount: $40.54',
      calculated: '• Calculated price: $81.08',
      formula: '200% x $40.54 = $81.08',
      note: 'Default allowable amounts are pulled from the Medicare fee schedule.'
    },
    dental: {
      example: 'Example:',
      multiplier: '• Multiplier is set to: 200%',
      service: '• Service: D1110 — Prophylaxis, adult',
      allowable: '• Allowable amount: $92.00',
      calculated: '• Calculated price: $184.00',
      formula: '200% x $92.00 = $184.00'
    }
  },
  allowable: {
    medical: {
      example: 'Example:',
      multiplier: '• Multiplier is set to: 110%',
      service: '• Service: 99212 — Established patient office visit (10 - 19 min)',
      allowable: '• Allowable amount: $40.54',
      calculated: '• Adjusted allowable: $44.59',
      formula: '110% x $40.54 = $44.59',
      note: 'Default allowable amounts are pulled from the Medicare fee schedule.'
    },
    dental: {
      example: 'Example:',
      multiplier: '• Multiplier is set to: 110%',
      service: '• Service: D1110 — Prophylaxis, adult',
      allowable: '• Allowable amount: $92.00',
      calculated: '• Adjusted allowable: $101.20',
      formula: '110% x $92.00 = $101.20'
    }
  }
};

const PriceAdjustments = ({
  standardChargeMultiplier,
  selfPayMultiplier,
  allowableMultiplier,
  onStandardChargeChange,
  onSelfPayChange,
  onAllowableChange,
  isDental = false
}) => {
  const priceContent = isDental ? TOOLTIP_CONTENT.price.dental : TOOLTIP_CONTENT.price.medical;
  const allowableContent = isDental
    ? TOOLTIP_CONTENT.allowable.dental
    : TOOLTIP_CONTENT.allowable.medical;

  const TooltipContent = ({ content }) => (
    <div className="text-left">
      <div>{content.example}</div>
      <div>{content.multiplier}</div>
      <div>{content.service}</div>
      <div>{content.allowable}</div>
      <div>{content.calculated}</div>
      <div className="mt-2">{content.formula}</div>
      {content.note && <div className="mt-2">{content.note}</div>}
    </div>
  );

  return (
    <div>
      <div className="mb-2 text-base font-medium text-neutral-900">Service price adjustments</div>
      <div className="mb-4 text-sm text-neutral-700">
        {isDental
          ? 'Adjust your fee schedule allowable amounts and the service prices derived from them by applying percentage-based multipliers.'
          : 'Adjust allowable amounts and the service prices derived from them by applying percentage-based multipliers.'}{' '}
        Prices update in real time as you enter values.
      </div>
      <div className="flex items-start gap-6">
        <div className="flex flex-1 flex-col gap-1">
          <div className="flex items-center justify-between">
            <Typography className="text-sm font-medium text-neutral-900">
              Allowable amount multiplier
            </Typography>
            <Tippy content={<TooltipContent content={allowableContent} />}>
              <span className="inline-flex cursor-pointer">
                <Icon icon="info-circle-mono" size={16} color="neutral" shade={500} />
              </span>
            </Tippy>
          </div>
          <Input
            className="w-full"
            value={allowableMultiplier}
            actionButtonText="%"
            name="allowableMultiplier"
            onChange={onAllowableChange}
            placeholder="0"
          />
        </div>
        <div className="flex flex-1 flex-col gap-1">
          <div className="flex items-center justify-between">
            <Typography className="text-sm font-medium text-neutral-900">
              Standard charge multiplier
            </Typography>
            <Tippy content={<TooltipContent content={priceContent} />}>
              <span className="inline-flex cursor-pointer">
                <Icon icon="info-circle-mono" size={16} color="neutral" shade={500} />
              </span>
            </Tippy>
          </div>
          <Input
            className="w-full"
            value={standardChargeMultiplier}
            actionButtonText="%"
            name="standardChargeMultiplier"
            onChange={onStandardChargeChange}
            placeholder="0"
          />
        </div>
        <div className="flex flex-1 flex-col gap-1">
          <div className="flex items-center justify-between">
            <Typography className="text-sm font-medium text-neutral-900">
              Self pay multiplier
            </Typography>
            <Tippy content={<TooltipContent content={priceContent} />}>
              <span className="inline-flex cursor-pointer">
                <Icon icon="info-circle-mono" size={16} color="neutral" shade={500} />
              </span>
            </Tippy>
          </div>
          <Input
            className="w-full"
            value={selfPayMultiplier}
            actionButtonText="%"
            name="selfPayMultiplier"
            onChange={onSelfPayChange}
            placeholder="0"
          />
        </div>
      </div>
    </div>
  );
};

export default PriceAdjustments;
